/**
 * VariableFormDialog - Reusable dialog for creating/editing a FunnelVariable
 *
 * Popup form similar to Create Task dialog. Can be used independently in:
 * - Product page (project-level variables)
 * - Strategy form (strategy-level variables)
 * - Block form (variable assignment)
 *
 * The dialog manages its own form state internally.
 * Caller provides onSave callback with the final variable data.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Checkbox,
  IconButton,
  Box,
  Typography,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { PRODUCT_VARIABLES } from '@test-kit/selectors/variable-privacy';
import { AnalyticsPrivacyDecisionField } from '@modules/v2-variable-utils/components/AnalyticsPrivacyDecisionField';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

/** Variable type options matching V2 VariableType enum */
const VARIABLE_TYPES = [
  { value: 'string', label: 'Text', description: 'Free-form text input' },
  { value: 'number', label: 'Number', description: 'Numeric value' },
  { value: 'boolean', label: 'Yes/No', description: 'True or false' },
  { value: 'date', label: 'Date', description: 'Date selection' },
  { value: 'enum', label: 'Single Choice', description: 'Pick one from options' },
  { value: 'enum[]', label: 'Multiple Choice', description: 'Pick multiple from options' },
] as const;

/** Variable data for the form */
export interface VariableFormData {
  id: string;
  name: string;
  type: string;
  required: boolean;
  /** Explicit privacy decision. true = never include this variable value in analytics. */
  excludeFromAnalytics?: boolean;
  description: string;
  taskIds: string[];
  options: Array<{ id: string; label: string }>;
}

/** Task reference for taskIds selector */
export interface TaskRef {
  id: string;
  name: string;
}

export interface VariableFormDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Close dialog */
  onClose: () => void;
  /** Save callback — receives the variable data */
  onSave: (variable: VariableFormData) => void | Promise<void>;
  /** Initial data for editing (null = create mode) */
  initialData?: VariableFormData | null;
  /** Available tasks for taskIds assignment */
  tasks?: TaskRef[];
  /** Existing variable IDs (for uniqueness validation) */
  existingIds?: string[];
  /** Whether save is in progress */
  isSaving?: boolean;
  saveError?: string | null;
  /** Pre-select this taskId when creating (from task context) */
  defaultTaskId?: string;
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

/** Generate a slug from name: "User Goal" → "user_goal", "Цель" → "цель" */
function nameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, '_')
    .replace(/^_+|_+$/g, '');
}

const DEFAULT_FORM: VariableFormData = {
  id: '',
  name: '',
  type: 'string',
  required: true,
  excludeFromAnalytics: undefined,
  description: '',
  taskIds: [],
  options: [],
};

const isEnumType = (type: string) => type === 'enum' || type === 'enum[]';

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export const VariableFormDialog: React.FC<VariableFormDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData = null,
  tasks = [],
  existingIds = [],
  isSaving = false,
  saveError = null,
  defaultTaskId,
}) => {
  const isEditMode = !!initialData;
  const [form, setForm] = useState<VariableFormData>(DEFAULT_FORM);

  // Reset form when dialog opens, pre-select task if provided
  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({
          ...initialData,
          excludeFromAnalytics: initialData.excludeFromAnalytics,
          options: initialData.options || [],
          taskIds: initialData.taskIds || [],
        });
      } else {
        setForm({
          ...DEFAULT_FORM,
          taskIds: defaultTaskId ? [defaultTaskId] : [],
        });
      }
    }
  }, [open, initialData, defaultTaskId]);

  // Auto-generate ID from name (only in create mode)
  const autoId = useMemo(() => {
    if (isEditMode) return form.id;
    return nameToSlug(form.name);
  }, [form.name, form.id, isEditMode]);

  const idConflict = !isEditMode && autoId && existingIds.includes(autoId);
  const canSave = form.name.trim().length > 0
    && autoId.length > 0
    && !idConflict
    && typeof form.excludeFromAnalytics === 'boolean';

  const handleSave = useCallback(() => {
    if (!canSave || isSaving) return;
    onSave({
      ...form,
      id: autoId || form.id,
    });
  }, [form, autoId, canSave, onSave, isSaving]);

  // ── Field handlers ──

  const updateField = useCallback(<K extends keyof VariableFormData>(field: K, value: VariableFormData[K]) => {
    if (!isSaving) setForm(prev => ({ ...prev, [field]: value }));
  }, [isSaving]);

  const handleAddOption = useCallback(() => {
    let idx = form.options.length;
    while (form.options.some(option => option.id === `option_${idx}`)) idx++;
    updateField('options', [...form.options, { id: `option_${idx}`, label: '' }]);
  }, [form.options, updateField]);

  const handleUpdateOption = useCallback((index: number, label: string) => {
    const options = [...form.options];
    options[index] = { ...options[index], label };
    updateField('options', options);
  }, [form.options, updateField]);

  const handleDeleteOption = useCallback((index: number) => {
    updateField('options', form.options.filter((_, i) => i !== index));
  }, [form.options, updateField]);

  const toggleTaskId = useCallback((taskId: string) => {
    console.warn('🟡 toggleTaskId:', taskId, 'current:', form.taskIds);
    const current = form.taskIds;
    const updated = current.includes(taskId)
      ? current.filter(id => id !== taskId)
      : [...current, taskId];
    updateField('taskIds', updated);
  }, [form.taskIds, updateField]);

  return (
    <Dialog
      data-testid={PRODUCT_VARIABLES.dialog}
      open={open}
      onClose={() => { if (!isSaving) onClose(); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'grid',
          gridTemplateColumns: '40px 1fr 40px',
          alignItems: 'center',
          gap: 1,
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <IconButton
            onClick={onClose}
            disabled={isSaving}
            size="small"
            aria-label="close dialog"
            sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          component="span"
          sx={{
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {isEditMode ? 'Edit Variable' : 'Create New Variable'}
        </Box>
        <Box />
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
        {saveError && <Typography role="alert" color="error" sx={{ mb: 2 }}>{saveError}</Typography>}
        <Box component="fieldset" disabled={isSaving} sx={{ display: 'flex', flexDirection: 'column', gap: 2, border: 0, p: 0, m: 0, minWidth: 0 }}>

          {/* Name + Type */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              data-testid={PRODUCT_VARIABLES.nameInput}
              fullWidth
              label="Variable Name"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
              placeholder="e.g. User Goal"
              autoFocus
            />
            <FormControl disabled={isSaving} sx={{ minWidth: 160 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.type}
                label="Type"
                onChange={(e) => {
                  updateField('type', e.target.value);
                  if (!isEnumType(e.target.value)) updateField('options', []);
                }}
              >
                {VARIABLE_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>
                    <Box>
                      <Typography variant="body2">{t.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Auto-generated ID */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Variable ID: <code>{autoId || '(auto-generated from name)'}</code>
              {idConflict && (
                <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                  ID already exists
                </Typography>
              )}
            </Typography>
          </Box>

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            multiline
            rows={2}
            placeholder="Describe what this variable represents (used as AI context)"
          />

          {/* Required toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={form.required}
                onChange={(e) => updateField('required', e.target.checked)}
                size="small"
              />
            }
            label="Required (must be collected from user)"
          />

          {/* Analytics privacy decision */}
          <AnalyticsPrivacyDecisionField
            value={form.excludeFromAnalytics}
            onChange={(value) => updateField('excludeFromAnalytics', value)}
            testIdPrefix={PRODUCT_VARIABLES.decisionPrefix}
            disabled={isSaving}
          />

          {/* Task Scope */}
          {tasks.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Task Scope
              </Typography>
              {form.taskIds.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Global — collected for all tasks
                </Typography>
              )}
              <Stack spacing={0}>
                {tasks.map((task) => (
                  <FormControlLabel
                    key={task.id}
                    control={
                      <Checkbox
                        checked={form.taskIds.includes(task.id)}
                        onChange={() => toggleTaskId(task.id)}
                        size="small"
                      />
                    }
                    label={task.name}
                    sx={{ ml: 0 }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Enum Options */}
          {isEnumType(form.type) && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Options
              </Typography>
              {form.options.map((opt, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={opt.label}
                    onChange={(e) => handleUpdateOption(idx, e.target.value)}
                    placeholder={`Option ${idx + 1}`}
                  />
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 80, color: 'text.secondary' }}>
                    {opt.id}
                  </Typography>
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => handleDeleteOption(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
              <Button startIcon={<AddIcon />} size="small" onClick={handleAddOption}>
                Add Option
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          data-testid={PRODUCT_VARIABLES.saveButton}
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || isSaving}
        >
          {isEditMode ? 'Save Changes' : 'Create Variable'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
